import { useState } from 'react'
import Decimal from 'decimal.js'
import { Users } from 'lucide-react'
import {
  Banner,
  Button,
  Card,
  EmptyState,
  ErrorState,
  MoneyValue,
  Skeleton,
  StatusBadge,
  Toggle,
  TextField,
} from '@shared/design-system/ui'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { toast } from '@shared/lib/toast/toast-store'
import { useAdminReferrals, useUpdateReferralSettings } from '../api/referrals'
import * as styles from './referrals.css'

export function AdminReferralsPanel({
  userId,
  canManagePermission,
  canManageRate,
}: {
  userId: string
  canManagePermission: boolean
  canManageRate: boolean
}) {
  const enabled = useFeatureFlag('referralProgram')
  const [offset, setOffset] = useState(0)
  const [percentage, setPercentage] = useState<string | null>(null)
  const [percentageError, setPercentageError] = useState<string | undefined>()
  const query = useAdminReferrals(userId, offset, enabled)
  const mutation = useUpdateReferralSettings(userId)
  if (!enabled) return null
  if (query.isPending) return <Skeleton height={320} />
  if (query.isError)
    return (
      <ErrorState
        title="Não foi possível carregar as indicações"
        onRetry={() => void query.refetch()}
      />
    )
  const data = query.data
  const update = async (next: boolean) => {
    try {
      await mutation.mutateAsync({ enabled: next })
      toast.success(
        next ? 'Usuário habilitado para indicar.' : 'Indicações desabilitadas para este usuário.',
      )
    } catch {
      toast.error('Não foi possível confirmar a alteração. Atualize e tente novamente.')
    }
  }
  const savePercentage = async () => {
    const normalized = (percentage ?? data.percentage).trim().replace(',', '.')
    if (
      !/^\d{1,3}(\.\d{1,3})?$/.test(normalized) ||
      new Decimal(normalized).lte(0) ||
      new Decimal(normalized).gt(data.maximumPercentage)
    ) {
      setPercentageError(
        `Informe um percentual maior que 0 e até ${Number(data.maximumPercentage).toLocaleString('pt-BR')}, com no máximo três casas decimais.`,
      )
      return
    }
    try {
      await mutation.mutateAsync({ percentage: new Decimal(normalized).toFixed(3) })
      setPercentage(null)
      setPercentageError(undefined)
      toast.success('Taxa de indicação atualizada para novos pagamentos.')
    } catch {
      toast.error('Não foi possível confirmar a alteração da taxa. Atualize e tente novamente.')
    }
  }
  return (
    <section className={styles.stack} data-testid="admin-referrals-panel" role="tabpanel">
      <Card className={styles.stack}>
        <div className={styles.header}>
          <h2 className={styles.heading}>Indique e ganhe</h2>
          <StatusBadge
            tone={data.enabled ? 'success' : 'neutral'}
            label={data.enabled ? 'Habilitado' : 'Não habilitado'}
          />
        </div>
        <div className={styles.header}>
          <div className={styles.person}>
            <strong>Permitir que este usuário indique vendedores</strong>
            <p className={styles.copy}>
              A habilitação vale para novas vendas. Desabilitar preserva vínculos e comissões já
              geradas.
            </p>
          </div>
          {canManagePermission ? (
            <Toggle
              checked={data.enabled}
              disabled={mutation.isPending || query.isFetching}
              ariaLabel="Habilitar indicações"
              data-testid="admin-referrals-permission"
              onChange={(next) => void update(next)}
            />
          ) : null}
        </div>
        <Banner
          tone="info"
          title={`Comissão de ${Number(data.percentage).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}% da venda`}
        >
          Paga exclusivamente pela taxa VendePay, limitada ao líquido disponível após os custos do
          pagamento. Reembolsos e chargebacks estornam proporcionalmente a comissão.
        </Banner>
        <Banner
          tone="info"
          title="Habilitar indicações não libera repasses"
          data-testid="admin-referrals-receiving-guidance"
        >
          O usuário pode indicar e acumular comissões antes da aprovação cadastral, inclusive em
          novas vendas e renovações elegíveis. O repasse aguarda a conta estar apta a receber e a
          conclusão do processamento. Pendências posteriores no cadastro não apagam comissões já
          registradas. Reembolsos e chargebacks continuam sujeitos a estorno. Consulte a situação
          cadastral na aba Cadastro e KYC antes de orientar o usuário sobre recebimentos.
        </Banner>
        {canManageRate ? (
          <form
            className={styles.stack}
            data-testid="admin-referrals-rate-form"
            onSubmit={(event) => {
              event.preventDefault()
              void savePercentage()
            }}
          >
            <TextField
              label="Comissão de indicação (%)"
              inputMode="decimal"
              value={
                percentage ??
                Number(data.percentage).toLocaleString('pt-BR', { maximumFractionDigits: 3 })
              }
              onChange={(event) => {
                setPercentage(event.target.value)
                setPercentageError(undefined)
              }}
              error={percentageError}
              disabled={mutation.isPending || query.isFetching}
              data-testid="admin-referrals-rate"
            />
            <p className={styles.copy}>
              Padrão de 0,5%. Limite atual: {Number(data.maximumPercentage).toLocaleString('pt-BR')}
              %. Alterações valem para pagamentos posteriores. Vendas anteriores preservam a taxa
              registrada.
            </p>
            <div>
              <Button
                type="submit"
                data-testid="admin-referrals-save-rate-btn"
                disabled={percentage === null || mutation.isPending || query.isFetching}
              >
                Salvar taxa
              </Button>
            </div>
          </form>
        ) : null}
      </Card>
      <div className={styles.stats} data-testid="admin-referrals-totals">
        <Card className={styles.metric}>
          <span className={styles.copy}>Vendedores indicados</span>
          <strong className={styles.title}>{data.total}</strong>
        </Card>
        {data.totals ? (
          <>
            <Card className={styles.metric}>
              <span className={styles.copy}>Comissões líquidas</span>
              <MoneyValue amount={data.totals.net} currency="BRL" size="xl" />
            </Card>
            <Card className={styles.metric}>
              <span className={styles.copy}>Aguardando repasse</span>
              <MoneyValue amount={data.totals.pending} currency="BRL" size="xl" />
            </Card>
          </>
        ) : null}
      </div>
      <Card data-testid="admin-referrals-list">
        <div className={styles.header}>
          <h2 className={styles.heading}>Vendedores indicados</h2>
          <Button
            variant="secondary"
            data-testid="admin-referrals-refresh-btn"
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
          >
            Atualizar
          </Button>
        </div>
        {data.items.length ? (
          data.items.map((item) => (
            <article
              className={styles.row}
              key={item.id}
              data-testid={`admin-referrals-row-${item.id}`}
            >
              <div className={styles.person}>
                <strong>{item.name}</strong>
                <span className={styles.copy}>
                  Desde {new Date(item.joinedAt).toLocaleDateString('pt-BR')}
                </span>
              </div>
              {item.earnings ? (
                <>
                  <div className={styles.person}>
                    <span className={styles.copy}>Comissão líquida</span>
                    <MoneyValue amount={item.earnings.net} currency="BRL" />
                  </div>
                  <div className={styles.person}>
                    <span className={styles.copy}>Estornada</span>
                    <MoneyValue amount={item.earnings.reversed} currency="BRL" />
                  </div>
                </>
              ) : null}
            </article>
          ))
        ) : (
          <EmptyState
            icon={<Users />}
            title="Nenhuma indicação registrada"
            description="Novos vendedores aparecem após se cadastrarem pelo link de indicação deste usuário."
          />
        )}
        {offset > 0 || data.nextOffset !== null ? (
          <div className={styles.header}>
            <Button
              variant="secondary"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - 20))}
              data-testid="admin-referrals-previous-btn"
            >
              Anterior
            </Button>
            <Button
              variant="secondary"
              disabled={data.nextOffset === null}
              onClick={() => {
                if (data.nextOffset !== null) setOffset(data.nextOffset)
              }}
              data-testid="admin-referrals-next-btn"
            >
              Próxima
            </Button>
          </div>
        ) : null}
        <p className={styles.copy}>
          Valores líquidos já descontam estornos. Repasse não significa disponibilidade bancária.
        </p>
      </Card>
    </section>
  )
}
